import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import {
  DocumentChartBarIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  UserIcon,
  SparklesIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { reportsService } from '../services/api';

const ReportsList = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [groupByPatient, setGroupByPatient] = useState(false);
  const [groupByCompany, setGroupByCompany] = useState(false);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      const data = await reportsService.getAll();
      setReports(data);
    } catch (err) {
      console.error('Erro ao carregar relatórios:', err);
      // Mock data para demonstração
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredReports = reports.filter((report) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      report.patientName?.toLowerCase().includes(searchLower) ||
      report.doctorName?.toLowerCase().includes(searchLower) ||
      report.type?.toLowerCase().includes(searchLower)
    );
  });

  // Agrupar relatórios por paciente
  const groupedReports = filteredReports.reduce((acc, report) => {
    const patientId = report.patientId || 'unknown';
    if (!acc[patientId]) {
      acc[patientId] = {
        patientId,
        patientName: report.patientName || 'Paciente Desconhecido',
        reports: [],
      };
    }
    acc[patientId].reports.push(report);
    return acc;
  }, {});

  const patientGroups = Object.values(groupedReports).sort(
    (a, b) => b.reports.length - a.reports.length
  );

  // Agrupar relatórios por empresa
  const groupedByCompany = filteredReports.reduce((acc, report) => {
    const companyName = report.patientCompany || 'Sem Empresa';
    if (!acc[companyName]) {
      acc[companyName] = {
        companyName,
        reports: [],
      };
    }
    acc[companyName].reports.push(report);
    return acc;
  }, {});

  const companyGroups = Object.values(groupedByCompany).sort(
    (a, b) => b.reports.length - a.reports.length
  );

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-medical-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
              <DocumentChartBarIcon className="w-10 h-10 text-medical-600" />
              Laudos
            </h1>
            <p className="text-gray-600 mt-1">
              Gerencie e visualize todos os laudos audiométricos
            </p>
          </div>
          <Link
            to="/report-wizard"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-medical-500 to-tech-600 text-white rounded-lg font-medium hover:from-medical-600 hover:to-tech-700 transition-all shadow-lg hover:shadow-xl"
          >
            <PlusIcon className="w-5 h-5" />
            Novo Laudo
          </Link>
        </div>

        {/* Search and View Toggle */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Buscar por paciente, médico ou tipo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-transparent transition-all"
            />
          </div>
          <button
            onClick={() => {
              setGroupByPatient(!groupByPatient);
              if (!groupByPatient) setGroupByCompany(false);
            }}
            className={`px-6 py-3 rounded-lg font-medium transition-all ${
              groupByPatient
                ? 'bg-medical-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {groupByPatient ? 'Ver Lista Completa' : 'Agrupar por Paciente'}
          </button>
          <button
            onClick={() => {
              setGroupByCompany(!groupByCompany);
              if (!groupByCompany) setGroupByPatient(false);
            }}
            className={`px-6 py-3 rounded-lg font-medium transition-all ${
              groupByCompany
                ? 'bg-tech-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {groupByCompany ? 'Ver Lista Completa' : 'Agrupar por Empresa'}
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total de Laudos</p>
                <p className="text-3xl font-bold text-gray-900">{reports.length}</p>
              </div>
              <div className="w-12 h-12 bg-medical-100 rounded-lg flex items-center justify-center">
                <DocumentChartBarIcon className="w-6 h-6 text-medical-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Este Mês</p>
                <p className="text-3xl font-bold text-gray-900">
                  {
                    reports.filter((r) => {
                      const reportDate = new Date(r.createdAt);
                      const now = new Date();
                      return (
                        reportDate.getMonth() === now.getMonth() &&
                        reportDate.getFullYear() === now.getFullYear()
                      );
                    }).length
                  }
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CalendarIcon className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Pacientes Únicos</p>
                <p className="text-3xl font-bold text-gray-900">{patientGroups.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <UserIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        {filteredReports.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <DocumentChartBarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm ? 'Nenhum laudo encontrado' : 'Nenhum laudo gerado'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm
                ? 'Tente ajustar os termos de busca'
                : 'Comece gerando seu primeiro laudo audiométrico com IA'}
            </p>
            {!searchTerm && (
              <Link
                to="/report-wizard"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-purple-700 transition-all shadow-lg"
              >
                <PlusIcon className="w-5 h-5" />
                Gerar Primeiro Laudo
              </Link>
            )}
          </div>
        ) : groupByCompany ? (
          /* Vista Agrupada por Empresa */
          <div className="space-y-6">
            {companyGroups.map((group, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden">
                {/* Company Header */}
                <div className="bg-gradient-to-r from-tech-50 to-medical-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-tech-600 rounded-full flex items-center justify-center">
                        <DocumentChartBarIcon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-800">{group.companyName}</h3>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-tech-100 text-tech-800 mt-1">
                          {group.reports.length} {group.reports.length === 1 ? 'laudo' : 'laudos'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reports Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Data
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Paciente
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Médico
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                          Tipo
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {group.reports.map((report) => (
                        <tr key={report.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <CalendarIcon className="w-4 h-4 text-gray-400" />
                              <span className="text-sm text-gray-900">{formatDate(report.createdAt)}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900">{report.patientName || 'N/A'}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-600">{report.doctorName || 'N/A'}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                                report.type === 'nexo-causal'
                                  ? 'bg-orange-100 text-orange-800'
                                  : 'bg-blue-100 text-blue-800'
                              }`}
                            >
                              {report.type === 'nexo-causal' ? 'Nexo Causal' : 'Audiometria Simples'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => navigate(`/reports/${report.id}`)}
                                title="Visualizar laudo"
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium"
                              >
                                <EyeIcon className="w-4 h-4" />
                                Ver
                              </button>
                              <button
                                onClick={() => {
                                  if (report.pdfUrl) {
                                    window.open(report.pdfUrl, '_blank');
                                  } else {
                                    alert('PDF não disponível para este laudo.');
                                  }
                                }}
                                title="Baixar PDF"
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium"
                              >
                                <ArrowDownTrayIcon className="w-4 h-4" />
                                PDF
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        ) : groupByPatient ? (
          /* Vista Agrupada por Paciente */
          <div className="space-y-6">
            {patientGroups.map((group) => {
              const hasMultipleReports = group.reports.length >= 2;
              return (
                <div key={group.patientId} className="bg-white rounded-lg shadow-md overflow-hidden">
                  {/* Patient Header */}
                  <div className="bg-gradient-to-r from-medical-50 to-tech-50 px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-medical-600 rounded-full flex items-center justify-center">
                          <UserIcon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-800">{group.patientName}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-medical-100 text-medical-800">
                              {group.reports.length} {group.reports.length === 1 ? 'laudo' : 'laudos'}
                            </span>
                            {hasMultipleReports && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800">
                                <SparklesIcon className="w-3 h-3 mr-1" />
                                Análise Evolutiva Disponível
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {hasMultipleReports && (
                        <Link
                          to={`/reports/${group.patientId}/evolution`}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-all shadow-md hover:shadow-lg"
                        >
                          <ChartBarIcon className="w-5 h-5" />
                          Análise Evolutiva
                        </Link>
                      )}
                    </div>
                  </div>

                  {/* Reports Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                            Data
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                            Paciente
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                            Médico
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                            Tipo
                          </th>
                          <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">
                            Ações
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {group.reports.map((report) => (
                          <tr key={report.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-2">
                                <CalendarIcon className="w-4 h-4 text-gray-400" />
                                <span className="text-sm text-gray-900">{formatDate(report.createdAt)}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-gray-900">{report.patientName || 'N/A'}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-gray-600">{report.doctorName || 'N/A'}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                                  report.type === 'nexo-causal'
                                    ? 'bg-orange-100 text-orange-800'
                                    : 'bg-blue-100 text-blue-800'
                                }`}
                              >
                                {report.type === 'nexo-causal' ? 'Nexo Causal' : 'Audiometria Simples'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => navigate(`/reports/${report.id}`)}
                                  title="Visualizar laudo"
                                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium"
                                >
                                  <EyeIcon className="w-4 h-4" />
                                  Ver
                                </button>
                                <button
                                  onClick={() => {
                                    if (report.pdfUrl) {
                                      window.open(report.pdfUrl, '_blank');
                                    } else {
                                      alert('PDF não disponível para este laudo.');
                                    }
                                  }}
                                  title="Baixar PDF"
                                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium"
                                >
                                  <ArrowDownTrayIcon className="w-4 h-4" />
                                  PDF
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Vista de Lista Completa */
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Paciente
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Médico
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredReports.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <CalendarIcon className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-900">{formatDate(report.createdAt)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <UserIcon className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-900">{report.patientName || 'N/A'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600">{report.doctorName || 'N/A'}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                            report.type === 'nexo-causal'
                              ? 'bg-orange-100 text-orange-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {report.type === 'nexo-causal' ? 'Nexo Causal' : 'Audiometria Simples'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => navigate(`/reports/${report.id}`)}
                            title="Visualizar laudo"
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium"
                          >
                            <EyeIcon className="w-4 h-4" />
                            Ver
                          </button>
                          <button
                            onClick={() => {
                              if (report.pdfUrl) {
                                window.open(report.pdfUrl, '_blank');
                              } else {
                                alert('PDF não disponível para este laudo.');
                              }
                            }}
                            title="Baixar PDF"
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium"
                          >
                            <ArrowDownTrayIcon className="w-4 h-4" />
                            PDF
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ReportsList;
